import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import EmptyState from '../../components/EmptyState';
import { Plus, Layers, Loader2, LayoutDashboard, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Portfolios = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const fetchPortfolios = async () => {
    try {
      const res = await axios.get(`${API}/portfolios`);
      setPortfolios(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPortfolios(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/portfolios`, form);
      toast.success('Portfolio created');
      setShowModal(false);
      setForm({ name: '', code: '', description: '' });
      navigate(`/admin/portfolios/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create portfolio');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Layout><LoadingState message="Loading portfolios..." /></Layout>;

  return (
    <Layout>
      <div className="space-y-6" data-testid="portfolios-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Portfolios</h1>
            <p className="text-slate-600 dark:text-slate-400 mt-1">Group projects into portfolios and programs for enterprise oversight</p>
          </div>
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-md" data-testid="create-portfolio-button">
            <Plus className="w-5 h-5" /><span>New Portfolio</span>
          </button>
        </div>

        {portfolios.length === 0 ? (
          <EmptyState icon={Layers} title="No portfolios yet"
            description="Create a portfolio, then assign existing projects to it. Nothing is auto-assigned."
            action={<button onClick={() => setShowModal(true)} className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"><Plus className="w-5 h-5" /><span>Create Portfolio</span></button>} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolios.map((p) => (
              <div key={p.id} onClick={() => navigate(`/admin/portfolios/${p.id}`)} className="bg-white dark:bg-slate-900 rounded-xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow cursor-pointer" data-testid="portfolio-card">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 bg-indigo-100 dark:bg-indigo-900/40 rounded-lg flex items-center justify-center"><Layers className="w-6 h-6 text-indigo-600 dark:text-indigo-300" /></div>
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/app/portfolio/${p.id}/command`); }} className="flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100" data-testid={`command-center-${p.id}`}>
                    <LayoutDashboard className="w-3.5 h-3.5" />Command
                  </button>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{p.name}</h3>
                  <span className="text-xs font-mono text-slate-400">{p.code}</span>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2 min-h-[2.5rem]">{p.description || 'No description'}</p>
                <div className="mt-4 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 capitalize">
                  <Building2 className="w-3.5 h-3.5" />{p.status}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="create-portfolio-modal">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full shadow-2xl">
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4">New Portfolio</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Name</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" data-testid="portfolio-name-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Code (optional)</label>
                <input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="auto-generated if blank" className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" data-testid="portfolio-code-input" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Description</label>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg outline-none focus:ring-2 focus:ring-blue-500" data-testid="portfolio-description-input" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-lg hover:bg-slate-200" data-testid="cancel-portfolio-button">Cancel</button>
                <button type="submit" disabled={submitting} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="submit-portfolio-button">
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" />Creating...</> : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Portfolios;
